const PREFIX = "bb_throttle:";
/**
 * Default emit ceiling per key family and page: a render loop must never flood
 * PostHog, and one noisy family must never starve another.
 */
const DEFAULT_PAGE_LIMIT = 20;

/** `last === null`: never emitted yet (only suppressed by the page budget). */
interface ThrottleEntry {
	last: number | null;
	suppressed: number;
}

// Page-local truth. Every write lands here; sessionStorage only carries state across
// reloads in the same tab and may be missing, blocked or read-only at any time.
// "Page" means the document lifetime of this module: a full load or reload starts
// fresh, a client-side (App Router) navigation keeps the module and therefore the
// map and the budgets below. The budgets live in memory only, never in storage.
const memory = new Map<string, ThrottleEntry>();
/** Emits spent per key family on this page. */
const familyEmits = new Map<string, number>();

function storage(): Storage | null {
	try {
		// SSR / node may lack sessionStorage; blocked site data throws on mere access.
		return typeof sessionStorage === "undefined" ? null : sessionStorage;
	} catch {
		return null;
	}
}

function parseEntry(raw: string | null): ThrottleEntry | null {
	if (!raw) return null;
	try {
		const value = JSON.parse(raw) as Partial<ThrottleEntry> | null;
		const last = value?.last;
		const suppressed = value?.suppressed;
		if (last !== null && (typeof last !== "number" || !Number.isFinite(last))) return null;
		if (typeof suppressed !== "number" || !Number.isInteger(suppressed) || suppressed < 0) return null;
		return { last, suppressed };
	} catch {
		return null;
	}
}

function read(key: string): ThrottleEntry | null {
	const local = memory.get(key);
	if (local) return local;
	try {
		return parseEntry(storage()?.getItem(PREFIX + key) ?? null);
	} catch {
		return null;
	}
}

/** Family = the key up to its first `:`; a key without `:` is its own family. */
function familyOf(key: string): string {
	const colon = key.indexOf(":");
	return colon === -1 ? key : key.slice(0, colon);
}

function resolvePageLimit(value: number | undefined): number {
	return typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : DEFAULT_PAGE_LIMIT;
}

function write(key: string, entry: ThrottleEntry): void {
	memory.set(key, entry);
	try {
		storage()?.setItem(PREFIX + key, JSON.stringify(entry));
	} catch {
		// Quota or read-only private mode: the page keeps throttling from memory,
		// only a reload in this tab starts the window fresh.
	}
}

/**
 * Rate limit for diagnostic analytics events: one emit per `key` per `windowMs`.
 *
 * The first call for a key emits. Calls inside the window return `emit: false` and
 * are counted; the next call after the window emits again and hands over how many
 * calls were swallowed since the previous emit (`suppressed`), so the event can carry
 * the burst size instead of silently losing it. The window restarts at each emit.
 *
 * Independently of the window, each key FAMILY (the key up to its first `:`, e.g.
 * `loyalty_credit_result` for `loyalty_credit_result:error:…`) emits at most
 * `options.pageLimit` times per page (default 20; a missing, negative or fractional
 * value falls back to it). Callers of one family should pass the same limit. Budgets
 * are separate per family, so a flooding event never silences another one. Capped
 * calls count as suppressed and surface on a later page. A page is the document
 * lifetime: only a full load or reload resets the budgets, client-side navigation
 * does not. Calls swallowed by their window spend no budget.
 *
 * Fail-open towards measuring: a stored timestamp in the future (clock moved back) or
 * a corrupt entry is treated as "window elapsed". Window state lives in sessionStorage
 * under `bb_throttle:` so a reload in the same tab stays throttled; every access is
 * guarded, and without storage the module map carries the page alone.
 */
export function consumeThrottled(
	key: string,
	windowMs: number,
	now = Date.now(),
	options: { pageLimit?: number } = {},
): { emit: boolean; suppressed: number } {
	const entry = read(key);
	const elapsed = entry?.last == null ? null : now - entry.last;
	const inWindow = elapsed !== null && elapsed >= 0 && elapsed < windowMs;
	const pending = entry?.suppressed ?? 0;
	const family = familyOf(key);
	const spent = familyEmits.get(family) ?? 0;

	if (inWindow || spent >= resolvePageLimit(options.pageLimit)) {
		const suppressed = pending + 1;
		write(key, { last: entry?.last ?? null, suppressed });
		return { emit: false, suppressed };
	}

	familyEmits.set(family, spent + 1);
	write(key, { last: now, suppressed: 0 });
	return { emit: true, suppressed: pending };
}
